import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

/**
 * Minimal data-loading hook.
 *
 * No query library: this app has a dozen endpoints and no shared cache
 * requirements, so a hook with explicit reload and poll support is less
 * machinery to reason about than a cache with invalidation rules.
 */

/**
 * Anything thrown is normalised to an Error. Besides being more useful, it makes
 * `{error && <ErrorState .../>}` type-check: an `unknown` left-hand side makes
 * the whole expression `unknown`, which is not a valid React child.
 */
export function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  return new Error(typeof value === 'string' ? value : JSON.stringify(value));
}

export interface Resource<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** Refetch without clearing the current data, so the UI does not flash. */
  reload: () => Promise<void>;
  /** Local optimistic update. */
  set: (next: T) => void;
}

export function useResource<T>(
  path: string | null,
  options: { pollMs?: number; enabled?: boolean } = {},
): Resource<T> {
  const { pollMs, enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(Boolean(path) && enabled);

  // Guards against a slow response from a previous path overwriting a newer one.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!path || !enabled) return;
    const id = ++requestId.current;
    try {
      const result = await api.get<T>(path);
      if (id !== requestId.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(toError(err));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [path, enabled]);

  useEffect(() => {
    if (!path || !enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void load();
  }, [load, path, enabled]);

  useEffect(() => {
    if (!pollMs || !path || !enabled) return;
    const timer = window.setInterval(() => void load(), pollMs);
    return () => window.clearInterval(timer);
  }, [load, pollMs, path, enabled]);

  return { data, error, loading, reload: load, set: setData };
}

/**
 * Wraps a mutating call with pending state and error capture, so every action
 * button in the app behaves the same way.
 */
export function useAction<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
): {
  run: (...args: Args) => Promise<R | undefined>;
  pending: boolean;
  error: Error | null;
  reset: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const run = useCallback(
    async (...args: Args) => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        setError(toError(err));
        return undefined;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );

  return { run, pending, error, reset: () => setError(null) };
}

/** Debounces a value, used for the live preview so typing does not spam the API. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
